import * as services from '@services/document'
import * as types from '@types'
import { INVALID_CPF_ERROR } from '@content/consts/Documents'
import defaults from './defaults'

const DOCUMENT_SET = 'SET_DOCUMENT'

export default {
  state: {
    documents: { ...defaults },
    customDocuments: {},
    systemFields: {},
    customFields: {}
  },

  getters: {
    [types.DOCUMENT_GET_DOCUMENTS]: state => ({
      ...state.documents,
      ...state.customDocuments,
    })
  },

  mutations: {
    [types.DOCUMENT_RESET]: (state) => {
      state.documents = { ...defaults }
      state.customDocuments = {}
      state.systemFields = {}
      state.customFields = {}
    },

    [types.DOCUMENT_SET_STATUS]: (state, { type, status }) => {
      const document = state.documents[type] || state.customDocuments[type]

      if (!document) return

      document.status = status
    },

    [types.DOCUMENT_SET_FILES]: (state, { type, files }) => {
      const document = state.documents[type] || state.customDocuments[type]

      if (!document) return

      document.files = files?.map(file => ({ ...file, progress: 0, uploading: false })) || []
    },

    [types.DOCUMENT_SYSTEM_FIELDS]: (state, fields) => {
      state.systemFields = { ...(fields?.systemFields || {}) }
      state.customFields = { ...(fields?.customFields || {}) }

      Object.keys({ ...state.documents, ...state.customDocuments })
        .forEach(type => {
          const document = state.documents[type] || state.customDocuments[type]

          if (!document) return

          document.systemFields = { ...(state.systemFields?.[type] || {}) }
          document.customFields = { ...(state.customFields?.[type] || {}) }
        })
    },

    [types.DOCUMENT_CUSTOM_DOCUMENTS]: (state, customDocuments) => {
      state.customDocuments = customDocuments
        .reduce((acc, document) => ({
          ...acc,
          [document.id]: {
            ...(state.customDocuments[document.id] || {}),
            id: document.id,
            title: document.label,
            data: {},
            files: document.documentFiles,
            filesPlaceholder: [],
            filesLimit: 20,
            filesSlots: false,
            systemFields: {},
            customFields: { ...(state.customFields?.[document.id] || {}) },
            customFieldValues: document.customFieldValues || {},
            customFieldValueId: document.customFieldValueId,
          }
        }), {})
    },

    [DOCUMENT_SET]: (state, { type, data }) => {
      const {
        id,
        documentFiles: files,
        isOptional = false,
        customFieldValues = {},
        ...documentData
      } = data || {}

      state.documents[type] = {
        ...(state.documents[type] || {}),
        id,
        files,
        isOptional,
        customFieldValues,
        systemFields: { ...(state.systemFields?.[type] || {}) },
        customFields: { ...(state.customFields?.[type] || {}) },
        data: documentData,
      }
    },

    [types.DOCUMENT_SET_CUSTOM]: (state, data) => {
      const document = state.customDocuments[data.id]

      if (!document) return

      document.data = data
    },
  },

  actions: {
    [types.DOCUMENT_GET_DOCUMENTS]: async ({ dispatch }) => {
      return Promise.allSettled([
        dispatch(types.DOCUMENT_GET_RG),
        dispatch(types.DOCUMENT_GET_CPF),
        dispatch(types.DOCUMENT_GET_DRIVER_LICENSE),
        dispatch(types.DOCUMENT_GET_RESERVIST),
        dispatch(types.DOCUMENT_GET_ELECTORAL),
        dispatch(types.DOCUMENT_GET_CTPS),
      ])
    },

    [types.DOCUMENT_SYSTEM_FIELDS]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, fields ] = await services.getSystemFields(params)

      if (!err) commit(types.DOCUMENT_SYSTEM_FIELDS, fields)

      return [ err, fields ]
    },

    [types.DOCUMENT_CUSTOM_DOCUMENTS]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, customDocuments ] = await services.getCustomDocuments(params)

      if (!err) commit(types.DOCUMENT_CUSTOM_DOCUMENTS, customDocuments)

      return [ err, customDocuments ]
    },

    [types.DOCUMENT_GET_RG]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid

      const [ err, data ] = await services.getGeneralRegisters({ employeeId, companyId })

      if (!err && data) commit(DOCUMENT_SET, { type: 'rg', data })

      return [ err, data ]
    },

    [types.DOCUMENT_SET_RG]: async ({ state, commit, getters, dispatch }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.documents?.rg?.customFields || {}
      const params = { employeeId, companyId, schema }

      const [ err, document ] = await services.updateGeneralRegisters(data, params)

      if (!err) commit(DOCUMENT_SET, { type: 'rg', data: document })

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      return [ err, document ]
    },

    [types.DOCUMENT_GET_CPF]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, data ] = await services.getCpf(params)

      if (!err && data) commit(DOCUMENT_SET, { type: 'cpf', data })

      return [ err, data ]
    },

    [types.DOCUMENT_GET_RESERVIST]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, data ] = await services.getReservist(params)

      if (!err && data) commit(DOCUMENT_SET, { type: 'reservist', data })

      return [ err, data ]
    },

    [types.DOCUMENT_SET_RESERVIST]: async ({ state, commit, getters, dispatch }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.documents?.reservist?.customFields || {}
      const params = { employeeId, companyId, schema }

      const [ err, document ] = await services.updateReservist(data, params)

      if (!err) commit(DOCUMENT_SET, { type: 'reservist', data: document })

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      return [ err, document ]
    },

    [types.DOCUMENT_FORM_FEEDBACK]: async ({ dispatch }, err) => {
      dispatch('FEEDBACKS_ADD', {
        type: err ? 'error' : 'success',
        message: err ? 'Erro' : 'Documento',
        highlighted: err
          ? 'ao salvar o documento. Tente novamente mais tarde.'
          : 'salvo com sucesso.'
      })
    },

    [types.DOCUMENT_SET_CNH]: async ({ state, commit, getters, dispatch }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.documents?.driver_license?.customFields || {}

      const [ err, document ] = await services.updateDriverLicense(
        data,
        { employeeId, companyId, schema }
      )

      if (!err) commit(DOCUMENT_SET, { type: 'driver_license', data: document })

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      return [ err, document ]
    },

    [types.DOCUMENT_GET_DRIVER_LICENSE]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid

      const [ err, data ] = await services.getDriverLicense({ employeeId, companyId })

      if (!err && data) commit(DOCUMENT_SET, { type: 'driver_license', data })

      return [ err, data ]
    },

    [types.DOCUMENT_SET_CPF]: async ({ state, commit, getters, dispatch }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.documents?.cpf?.customFields || {}

      const [ err, document ] = await services.updateCpf(data, { employeeId, companyId, schema })

      if (err) {
        const uniqueCpfErr = Array.isArray(err) && err
          .some(message => message.includes(INVALID_CPF_ERROR))

        dispatch('FEEDBACKS_ADD', {
          type: 'error',
          message: uniqueCpfErr ? 'CPF' : 'Erro',
          highlighted: uniqueCpfErr
            ? 'deve ser único dentro da empresa. Por favor, converse com o RH'
            : 'ao salvar o documento. Tente novamente mais tarde.'
        })

        return [ err, null ]
      }

      if (!err) commit(DOCUMENT_SET, { type: 'cpf', data: document })

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      return [ err, document ]
    },

    [types.DOCUMENT_GET_ELECTORAL]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid

      const [ err, data ] = await services.getElectoralCard({ employeeId, companyId })

      if (!err && data) commit(DOCUMENT_SET, { type: 'electoral_card', data })

      return [ err, data ]
    },

    [types.DOCUMENT_SET_ELECTORAL]: async ({ state, commit, getters, dispatch }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.documents?.electoral_card?.customFields || {}

      const [ err, document ] = await services.updateElectoralCard(
        data,
        { employeeId, companyId, schema }
      )

      if (!err) commit(DOCUMENT_SET, { type: 'electoral_card', data: document })

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      return [ err, document ]
    },

    [types.DOCUMENT_SET_CTPS]: async ({ state, commit, getters, dispatch }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.documents.ctps.customFields

      const [ err, document ] = await services.updateCtps(data, { employeeId, companyId, schema })

      if (!err) commit(DOCUMENT_SET, { type: 'ctps', data: document })

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      return [ err, document ]
    },

    [types.DOCUMENT_SET_CUSTOM]: async ({ state, getters, dispatch }, data) => {
      if (!data) return []

      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.customDocuments[data.id].customFields
      const params = { employeeId, companyId, schema }

      const [ err ] = await services.updateCustomDocument(data, params)

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      if (!err) {
        await dispatch(types.DOCUMENT_CUSTOM_DOCUMENTS)

        return [ null, data ]
      }

      return [ err, null ]
    },

    [types.DOCUMENT_VALIDATE_DOCUMENTS]: async ({ state, commit }, params) => {
      const { validated = [], errors = [] } = params

      let isValid = true

      Object.entries({ ...state.documents, ...state.customDocuments })
        .forEach(([ type ]) => {
          const valid = validated.includes(type)
          const error = errors.includes(type)

          if (!valid && !error)
            return

          commit(types.DOCUMENT_SET_STATUS, {
            type,
            status: valid ? 'success' : error ? 'error' : 'default'
          })

          if (isValid)
            isValid = valid && !error
        })

      return isValid
    },

    [types.DOCUMENT_GET_CTPS]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid

      const [ err, data ] = await services.getCtps({ employeeId, companyId })

      if (!err && data) commit(DOCUMENT_SET, { type: 'ctps', data })

      return [ err, data ]
    },

    [types.DOCUMENT_UPLOAD_FILES]: async ({ getters }, { id, files, type }) => {
      const employeeId = getters.getEmployeeUuid
      const params = { id, files, type, employeeId }

      const [ err ] = await services.uploadDocumentFiles(params)

      return [ err, null ]
    },
  }
}
